import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Plane, TrainFront, Bus, Ship, Car, CarTaxiFront, Hotel, MapPin, HandPlatter, Route, CalendarDays, Users, Lightbulb } from "lucide-react";
import axios from "axios";
import InviteFriends from "../components/InviteFriends";

/* PUBLIC trip summary (no login) — shows the plan only. Documents, PNRs,
   tickets, seats and contacts are never included by the backend. */
const ICONS = { flight: Plane, train: TrainFront, bus: Bus, ferry: Ship, car: Car, taxi: CarTaxiFront, hotel: Hotel, pickup: MapPin, reservation: HandPlatter, other: Route };

export default function TripShareView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/trips/shared-summary/${token}`)
      .then((r) => setData(r.data)).catch(() => setErr(true));
  }, [token]);

  if (err) return (
    <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="share-view-error">
      <h1 className="font-heading font-900 text-2xl mb-2">This share link is no longer active</h1>
      <p className="text-muted-foreground text-sm">The trip owner may have revoked it.</p>
      <Link to="/" className="inline-block mt-6 px-5 py-2.5 bg-primary text-white font-bold text-sm">Explore TravelerPulse</Link>
    </div>
  );
  if (!data) return <div className="p-24 text-center text-muted-foreground animate-pulse">Loading trip…</div>;

  const groups = [];
  (data.timeline || []).forEach((s) => {
    const d = s.date || "No date";
    const g = groups.find((x) => x.date === d);
    if (g) g.items.push(s); else groups.push({ date: d, items: [s] });
  });

  return (
    <div className="max-w-2xl mx-auto px-5 py-10" data-testid="trip-share-view">
      <div className="text-xs font-mono uppercase tracking-widest text-amber-700 mb-1">Shared trip plan{data.shared_by ? ` · from ${data.shared_by}` : ""}</div>
      <h1 className="font-heading font-900 text-3xl">{data.title}</h1>
      <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
        <CalendarDays className="w-4 h-4" /> {[data.start_date, data.end_date].filter(Boolean).join(" – ") || "Dates TBD"}
      </div>
      {data.stops?.length > 0 && <div className="text-sm mt-1 flex items-center gap-1.5"><MapPin className="w-4 h-4 text-primary" /> {data.stops.join(" → ")}</div>}
      {data.travelers?.length > 0 && (
        <div className="text-sm mt-1 flex items-center gap-1.5" data-testid="share-view-travelers">
          <Users className="w-4 h-4 text-primary" /> {data.travelers.map((t) => t.name).join(", ")}
        </div>
      )}
      {data.description && <p className="text-sm text-muted-foreground mt-3">{data.description}</p>}
      {data.budget_estimate && <div className="text-sm mt-2 inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-300 text-emerald-800 px-2.5 py-1 font-600" data-testid="share-view-budget">Budget: {data.budget_estimate}</div>}

      {/* Day-by-day itinerary (read-only) */}
      {data.day_plan?.length > 0 && (
        <div className="mt-8" data-testid="share-view-dayplan">
          <div className="font-mono text-xs font-bold uppercase tracking-widest text-emerald-700 mb-3">Day-by-day plan</div>
          <div className="space-y-3">
            {data.day_plan.map((d, i) => (
              <div key={i} className="border border-zinc-300 bg-white" data-testid={`share-view-day-${d.day || i + 1}`}>
                <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-100 bg-emerald-50/50">
                  <span className="shrink-0 w-8 h-8 bg-white border border-emerald-300 text-emerald-700 font-heading font-900 text-sm flex items-center justify-center">D{d.day || i + 1}</span>
                  <span className="font-700 text-sm">{d.title}</span>
                  {d.date && <span className="ml-auto font-mono text-xs text-muted-foreground">{d.date}</span>}
                </div>
                <div className="p-3 space-y-1.5">
                  {(d.activities || []).map((a, j) => (
                    <div key={j} className="flex items-start gap-2 text-sm">
                      {a.time && <span className="shrink-0 font-mono text-[11px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 mt-0.5">{a.time}</span>}
                      <span className="text-zinc-700">{a.text}{a.place ? <span className="text-muted-foreground"> · {a.place}</span> : null}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 space-y-5">
        {groups.map((g) => (
          <div key={g.date}>
            <div className="font-mono text-xs font-bold uppercase tracking-widest text-amber-700 mb-2">{g.date}</div>
            <div className="border-l-2 border-zinc-200 ml-2 space-y-3">
              {g.items.map((s, i) => {
                const Icon = ICONS[s.mode] || Route;
                return (
                  <div key={i} className="relative pl-5">
                    <span className="absolute -left-[9px] top-3 w-4 h-4 rounded-full bg-white border-2 border-primary" />
                    <div className="border border-zinc-300 bg-white p-3">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-primary" />
                        <span className="text-xs font-900 uppercase tracking-widest text-zinc-500">{s.mode}</span>
                        {s.ref && <span className="font-mono font-bold text-sm">{s.ref}</span>}
                        {s.carrier && <span className="text-xs text-muted-foreground">({s.carrier})</span>}
                        <span className="ml-auto font-mono text-sm font-bold">{s.time?.slice(0, 5)}</span>
                      </div>
                      <div className="text-sm mt-1 font-medium">
                        {s.from_place}{s.to_place && <span className="text-zinc-400"> → </span>}{s.to_place}
                        {(s.arrive_date || s.arrive_time) && <span className="text-xs text-muted-foreground ml-2">until {s.arrive_date || ""} {s.arrive_time?.slice(0, 5) || ""}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {groups.length === 0 && <p className="text-sm text-muted-foreground">No timeline shared yet.</p>}
      </div>

      {/* Suggest an activity (friends) */}
      <SuggestActivity token={token} days={data.day_plan || []} />

      <div className="mt-8"><InviteFriends /></div>

      <div className="mt-10 border-t border-zinc-200 pt-4 text-xs text-muted-foreground">
        Shared via <Link to="/" className="text-primary font-bold hover:underline">TravelerPulse.pro</Link> — plan, pack and track your own trips free.
      </div>
    </div>
  );
}

/* Friends can pitch an activity — the owner accepts it with one tap. */
function SuggestActivity({ token, days }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [day, setDay] = useState(1);
  const [time, setTime] = useState("");
  const [place, setPlace] = useState("");
  const [text, setText] = useState("");
  const [state, setState] = useState("idle"); // idle | busy | sent
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() || state === "busy") return;
    setState("busy");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/trips/shared-summary/${token}/suggest`,
        { name, day: Number(day), time, place, text });
      setState("sent");
    } catch { setState("idle"); }
  };
  if (state === "sent") {
    return (
      <div className="mt-8 border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-800 font-600" data-testid="suggest-sent">
        Suggestion sent! The trip owner can add it to the plan with one tap.
      </div>
    );
  }
  return (
    <div className="mt-8 border border-violet-300 bg-violet-50/40" data-testid="suggest-activity">
      <button onClick={() => setOpen((o) => !o)} data-testid="suggest-toggle"
        className="w-full text-left px-4 py-3 font-heading font-900 text-violet-900 flex items-center gap-2">
        <Lightbulb className="w-5 h-5 text-violet-600" /> Suggest an activity for this trip
        <span className="ml-auto text-sm font-400 text-violet-600">{open ? "Close" : "Add your idea →"}</span>
      </button>
      {open && (
        <form onSubmit={submit} className="px-4 pb-4 space-y-2">
          <div className="flex gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="suggest-name"
              className="flex-1 border border-zinc-300 bg-white px-2.5 py-1.5 text-sm outline-none focus:ring-1 focus:ring-violet-500" />
            <select value={day} onChange={(e) => setDay(e.target.value)} data-testid="suggest-day"
              className="border border-zinc-300 bg-white px-2 py-1.5 text-sm outline-none">
              {(days.length ? days : [{ day: 1 }]).map((d, i) => (
                <option key={i} value={d.day || i + 1}>Day {d.day || i + 1}</option>
              ))}
            </select>
            <input value={time} onChange={(e) => setTime(e.target.value)} placeholder="15:00" data-testid="suggest-time"
              className="w-16 border border-zinc-300 bg-white px-2 py-1.5 text-sm font-mono outline-none" />
          </div>
          <input value={place} onChange={(e) => setPlace(e.target.value)} placeholder="Place (optional)" data-testid="suggest-place"
            className="w-full border border-zinc-300 bg-white px-2.5 py-1.5 text-sm outline-none focus:ring-1 focus:ring-violet-500" />
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} required
            placeholder="Your idea — e.g. sunset kayaking at the lagoon" data-testid="suggest-text"
            className="w-full border border-zinc-300 bg-white px-2.5 py-1.5 text-sm outline-none focus:ring-1 focus:ring-violet-500" />
          <button type="submit" disabled={state === "busy" || !text.trim()} data-testid="suggest-submit"
            className="bg-violet-600 text-white px-4 py-2 text-sm font-700 disabled:opacity-50 hover:bg-violet-700 transition-colors">
            {state === "busy" ? "Sending…" : "Send suggestion"}
          </button>
        </form>
      )}
    </div>
  );
}
